import { truncate } from 'lodash';

import { Certificate } from '@legacy-types';

import ReviewCertificateButton from '@legacy-components/shared/certificates/ReviewCertificateButton';

import { Data } from '@shared/api/types';
import { CertificateStatusBadge } from '@shared/certificates/components/CertificateStatusBadge';
import { deleteCertificate } from '@shared/certificates/endpoints/deleteCertificate';
import { certificateStatusOptions } from '@shared/certificates/enums/CertificateStatus';
import { certificateTypeOptions } from '@shared/certificates/enums/CertificateType';
import { DataTable } from '@shared/ui/components/DataTable';
import { getUserOptions } from '@shared/users/endpoints/getUserOptions';
import { camelCaseToWords } from '@shared/utils/helpers';

type Columns = 'employee' | 'type' | 'status' | 'internalComment' | 'expiresAt' | 'updatedAt' | 'actions';

type CertificatesDataTableProps = {
  certificates: Data<Certificate>;
  columns?: Partial<{ [k in Columns]: boolean }>;
  admin?: boolean;
  avoidPagination?: boolean;
  withoutFilters?: boolean;
};

export function CertificatesDataTable({
  certificates,
  columns = {},
  admin = false,
  avoidPagination = false,
  withoutFilters = false,
}: CertificatesDataTableProps) {
  return (
    <DataTable<Certificate>
      data={certificates}
      columns={[
        {
          visible: columns.employee !== false,
          label: 'Employee',
          render: (row) => (
            <div>
              <div className="font-semibold">{row.companyUser?.user?.fullName}</div>
              <div className="text-sm text-gray-500">{row.companyUser?.company?.name}</div>
            </div>
          ),
          sortable: false,
          filterable: {
            type: 'select',
            icon: 'user',
            options: getUserOptions,
          },
        },
        {
          visible: columns.type !== false,
          name: 'type',
          render: (row) => (
            <div>
              <div className="font-semibold">{camelCaseToWords(row.type)}</div>
              <div className="text-sm text-gray-500">{truncate(row.description, { length: 60 })}</div>
            </div>
          ),
          filterable: {
            type: 'select',
            icon: 'tag',
            options: certificateTypeOptions,
          },
        },
        {
          visible: columns.status !== false,
          name: 'status',
          render: (row) => <CertificateStatusBadge certificate={row} />,
          filterable: {
            type: 'select',
            icon: 'check',
            options: certificateStatusOptions,
          },
        },
        {
          visible: admin && columns.internalComment !== false,
          name: 'internalComment',
          render: (row) => <em>{row.internalComment}</em>,
        },
        {
          visible: columns.expiresAt !== false,
          name: 'expiresAt',
          type: 'date',
        },
        {
          visible: columns.updatedAt !== false,
          name: 'updatedAt',
          type: 'datetime',
        },
        {
          visible: admin && columns.actions !== false,
          render: (row) => <ReviewCertificateButton certificate={row} onReview={certificates.refresh} />,
          sortable: false,
          type: 'actions',
        },
      ]}
      onDelete={deleteCertificate}
      avoidPagination={avoidPagination}
      withoutFilters={withoutFilters}
    />
  );
}
